import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { AlertTriangle, CreditCard, X } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

export function PaymentAlertBanner() {
  const { subscription, openBillingPortal, checkSubscriptionStatus } = useAuthStore();
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check for payment issues on mount
  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  // Don't show if no payment issue or dismissed
  if (!subscription?.payment_issue || dismissed) {
    return null;
  }

  const handleUpdatePayment = async () => {
    setLoading(true);
    try {
      const result = await openBillingPortal(window.location.origin);
      if (result.success && result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    } finally {
      setLoading(false);
    }
  };

  // Determine severity based on issue type
  const getSeverityStyles = () => {
    switch (subscription.payment_issue_type) {
      case 'paused':
        return 'bg-brick-soft text-brick';
      case 'past_due':
        return 'bg-ochre-soft text-ochre';
      case 'payment_failed':
      default:
        return 'bg-brick-soft text-brick';
    }
  };

  const getIcon = () => {
    switch (subscription.payment_issue_type) {
      case 'paused':
        return <AlertTriangle className="w-[18px] h-[18px] text-brick shrink-0" />;
      case 'past_due':
        return <AlertTriangle className="w-[18px] h-[18px] text-ochre shrink-0" />;
      default:
        return <CreditCard className="w-[18px] h-[18px] text-brick shrink-0" />;
    }
  };

  const getMessage = () => {
    if (subscription.payment_issue_message) {
      return subscription.payment_issue_message;
    }
    switch (subscription.payment_issue_type) {
      case 'paused':
        return 'Your subscription has been paused. Please update your payment method to resume service.';
      case 'past_due':
        return 'Your payment is past due. Please update your payment method to avoid service interruption.';
      case 'payment_failed':
      default:
        return 'We had trouble processing your payment. Please update your payment details.';
    }
  };

  return (
    <div className={cn(
      'relative flex items-center justify-between gap-4 px-4 py-3 mb-4 rounded-cards',
      getSeverityStyles()
    )}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {getIcon()}
        <p className="text-sm font-medium truncate">
          {getMessage()}
        </p>
      </div>
      
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          onClick={handleUpdatePayment}
          disabled={loading}
        >
          <CreditCard className="w-4 h-4 mr-2" />
          {loading ? 'Loading...' : 'Update payment'}
        </Button>
        
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded-pill hover:bg-ink/5 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 opacity-60" />
        </button>
      </div>
    </div>
  );
}
